"use client";

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Flex, Modal, Form, Select, Space, Mentions, Input, Button, message, Divider } from 'antd';
import { LeftOutlined } from '@ant-design/icons';
import Image from 'next/image'
import './index.scss'
import { countryCodes } from '@/lib/countryCodes'
import { createClientService } from '@/lib/http/client';
import { useData } from '@/context/hooks/DataProvider'
import { GoogleOAuthProvider, useGoogleLogin } from '@react-oauth/google';
import axios from 'axios';

// 工具函数：判断是否为纯数字
const isPureNumber = (str) => /^\d+$/.test(str);

// 登录状态常量
const LOGIN_STATUS = {
    LOGIN: 'LOGIN',
    REGISTER: 'REGISTER',
    FORGOT: 'FORGOT'
};

// 忘记密码状态常量
const FORGOT_STATUS = {
    VERIFY: 'verify',
    PASSWORD: 'password'
};

// 账户类型常量
const ACCOUNT_TYPES = {
    MOBILE: 'mobile',
    EMAIL: 'email'
};

// 验证码类型常量
const VERIFICATION_TYPES = {
    FORGOT_MOBILE: 14,
    FORGOT_EMAIL: 4,
    REGISTER_MOBILE: 15,
    REGISTER_EMAIL: 1
};

/**
 * 密码强度校验器
 */
const validatePasswordStrength = (_, value) => {
    if (!value) return Promise.resolve();

    // 长度校验
    if (value.length < 8 || value.length > 20) {
        return Promise.reject('Password must be 8 to 20 characters long!');
    }

    // 复杂度校验
    const hasLetter = /[a-zA-Z]/.test(value);
    const hasNumber = /[0-9]/.test(value);
    const hasSymbol = /[^a-zA-Z0-9]/.test(value);
    const typesCount = [hasLetter, hasNumber, hasSymbol].filter(Boolean).length;

    if (typesCount < 2) {
        return Promise.reject('Password must contain at least two of the following: letters, numbers, symbols!');
    }

    return Promise.resolve();
};

/**
 * Google登录按钮组件
 */
const GoogleLoginButton = ({ onLoginSuccess, onLoginError }) => {
    const googleLogin = useGoogleLogin({
        onSuccess: async (tokenResponse) => {
            try {
                const userInfo = await axios.get(
                    'https://www.googleapis.com/oauth2/v3/userinfo',
                    { headers: { Authorization: `Bearer ${tokenResponse.access_token}` } }
                );

                const { email, sub, name } = userInfo.data;

                await createClientService().post('mtop.login.google.login', {
                    email,
                    nikeName: name,
                    googleId: sub
                });

                onLoginSuccess?.();
            } catch (error) {
                onLoginError?.(error);
            }
        },
        onError: (error) => console.error('Google Login Failed:', error),
        onNonOAuthError: (error) => console.error('Google Non-OAuth Error:', error),
    });

    return (
        <img
            onClick={googleLogin}
            src="https://public-read-bkt-oss.oss-cn-hangzhou.aliyuncs.com/yibuy/admin/google_icon%402x.png"
            alt="Google Login"
        />
    );
};

/**
 * 自定义Hook：倒计时逻辑
 */
const useCountdown = (initialCount = 0) => {
    const [count, setCount] = useState(initialCount);
    const [timer, setTimer] = useState(null);

    const startCountdown = useCallback((seconds = 60) => {
        if (timer) return;

        setCount(seconds);
        const timerId = setInterval(() => {
            setCount(prev => {
                if (prev <= 1) {
                    clearInterval(timerId);
                    setTimer(null);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        setTimer(timerId);
    }, [timer]);

    const resetCountdown = useCallback(() => {
        if (timer) {
            clearInterval(timer);
            setTimer(null);
        }
        setCount(0);
    }, [timer]);

    return {
        count,
        startCountdown,
        resetCountdown,
        isActive: count > 0
    };
};

/**
 * 自定义Hook：表单输入样式管理
 */
const useInputStyles = () => {
    const [inputStyles, setInputStyles] = useState({
        account: false,
        password: false,
        code: false,
        newPwd: false,
    });

    const setInputFocus = useCallback((type) => {
        setTimeout(() => {
            setInputStyles(prev => ({ ...prev, [type]: true }));
        }, 10);
    }, []);

    const setInputBlur = useCallback((type) => {
        setInputStyles(prev => ({ ...prev, [type]: false }));
    }, []);

    return {
        inputStyles,
        setInputFocus,
        setInputBlur
    };
};

/**
 * 主登录模态框组件
 */
export default function LoginModal({ showLoginModal, onClose }) {
    const { login } = useData();
    const [form] = Form.useForm();
    const [open, setOpen] = useState(showLoginModal);

    // 使用自定义Hook管理输入样式
    const { inputStyles, setInputFocus, setInputBlur } = useInputStyles();

    // 使用自定义Hook管理倒计时
    const { count, startCountdown, resetCountdown, isActive } = useCountdown();

    const [accountType, setAccountType] = useState(ACCOUNT_TYPES.MOBILE);
    const [countryCode, setCountryCode] = useState('+86');
    const [accountValue, setAccountValue] = useState('');

    // 登录状态管理
    const [loginStatus, setLoginStatus] = useState(LOGIN_STATUS.LOGIN);
    const [forgotStatus, setForgotStatus] = useState(FORGOT_STATUS.VERIFY);
    const [verificationCode, setVerificationCode] = useState('');

    // 根据登录状态设置标题
    const titleText = useMemo(() => {
        switch (loginStatus) {
            case LOGIN_STATUS.LOGIN: return 'Login';
            case LOGIN_STATUS.REGISTER: return 'Create an Account';
            case LOGIN_STATUS.FORGOT: return 'Reset Password';
            default: return 'Login';
        }
    }, [loginStatus]);

    // 根据账户值判断账户类型
    useEffect(() => {
        setAccountType(isPureNumber(accountValue) ? ACCOUNT_TYPES.MOBILE : ACCOUNT_TYPES.EMAIL);
    }, [accountValue]);

    // 表单字段过滤函数
    const filterEmail = useCallback((str) => {
        return str ? str.split('@')[0] : '';
    }, []);

    // 处理账户输入变化
    const handleAccountChange = useCallback((value) => {
        setAccountValue(value);
    }, []);

    // 获取验证码
    const handleGetCode = useCallback(async () => {
        if (!accountValue) {
            message.error('Please input your Mobile Number/Email!');
            return;
        }

        const type = loginStatus === LOGIN_STATUS.FORGOT
            ? (accountType === ACCOUNT_TYPES.MOBILE
                ? VERIFICATION_TYPES.FORGOT_MOBILE
                : VERIFICATION_TYPES.FORGOT_EMAIL)
            : (accountType === ACCOUNT_TYPES.MOBILE
                ? VERIFICATION_TYPES.REGISTER_MOBILE
                : VERIFICATION_TYPES.REGISTER_EMAIL);

        try {
            if (accountType === ACCOUNT_TYPES.MOBILE) {
                await createClientService().get("mtop.app.getVerificationCode", {
                    data: { countryCode, phone: accountValue, type }
                });
            } else {
                await createClientService().get("mtop.app.getEmailCode", {
                    data: { email: accountValue, type }
                });
            }

            message.success('Verification code sent successfully');
            startCountdown();
        } catch (err) {
            message.error(err?.msg || 'Failed to send verification code');
        }
    }, [accountValue, accountType, countryCode, loginStatus, startCountdown]);

    // 登录成功回调
    const handleLoginSuccess = useCallback(async () => {
        try {
            const personalData = await createClientService().get('mtop.user.getUserPersonalData');
            const userData = {
                nickname: personalData?.baseInfo?.nickname || '',
                iconUrl: personalData?.baseInfo?.iconUrl || '',
            };
            login(userData);
            setOpen(false);
            onClose?.();
        } catch (error) {
            console.error('Login success callback error:', error);
        }
    }, [login, onClose]);

    // 处理登录/注册
    const handleLogin = useCallback(async () => {
        try {
            const fieldsToValidate = ['account', 'password'];
            if (loginStatus !== LOGIN_STATUS.LOGIN) {
                fieldsToValidate.push('code');
            }
            const values = await form.validateFields(fieldsToValidate);
            const { password, code } = values;

            if (loginStatus === LOGIN_STATUS.LOGIN) {
                if (accountType === ACCOUNT_TYPES.MOBILE) {
                    await createClientService().post('mtop.user.login', {
                        mobile: accountValue,
                        pwd: password,
                        countryCode
                    });
                } else {
                    await createClientService().post('mtop.user.loginEmail', {
                        email: accountValue,
                        pwd: password
                    });
                }
            } else if (loginStatus === LOGIN_STATUS.REGISTER) {
                if (accountType === ACCOUNT_TYPES.MOBILE) {
                    await createClientService().post('mtop.user.register', {
                        mobile: accountValue,
                        pwd: password,
                        countryCode,
                        verificationCode: code,
                        roleType: 4,
                        nickname: `ycb_${new Date().getTime()}`
                    }, { v: '2.0' });

                    await createClientService().post('mtop.user.login', {
                        mobile: accountValue,
                        pwd: password,
                        countryCode
                    });
                } else {
                    await createClientService().post('mtop.user.i18n.emailRegister', {
                        email: accountValue,
                        pwd: password,
                        verificationCode: code,
                        roleType: 4,
                        nickname: `ycb_${new Date().getTime()}`
                    }, { v: '2.0' });

                    await createClientService().post('mtop.user.loginEmail', {
                        email: accountValue,
                        pwd: password
                    });
                }
            }

            message.success('Login successful!');
            await handleLoginSuccess();
        } catch (error) {
            console.error('Login error:', error);
            message.error('Login failed, please check your input!');

            if (error.errorFields) {
                return
            }

            const errorMsg = error?.msg || 'Unknown error';

            // 根据错误信息设置表单字段错误
            if (loginStatus === LOGIN_STATUS.LOGIN) {
                form.setFields([{ name: ['password'], errors: [errorMsg] }]);
            } else {
                if (errorMsg.includes('number')) {
                    form.setFields([{ name: ['account'], errors: [errorMsg] }]);
                } else if (errorMsg.includes('code')) {
                    form.setFields([{ name: ['code'], errors: [errorMsg] }]);
                } else if (errorMsg.includes('Password')) {
                    form.setFields([{ name: ['password'], errors: [errorMsg] }]);
                }
            }
        }
    }, [form, loginStatus, accountType, accountValue, countryCode, handleLoginSuccess]);

    // 处理忘记密码 - 验证验证码
    const handleContinueNext = useCallback(async () => {
        try {

            await form.validateFields(['account', 'code']);

            const values = form.getFieldsValue();
            const type = accountType === ACCOUNT_TYPES.MOBILE
                ? VERIFICATION_TYPES.FORGOT_MOBILE
                : VERIFICATION_TYPES.FORGOT_EMAIL;

            if (accountType === ACCOUNT_TYPES.MOBILE) {
                await createClientService().get("mtop.app.getVerificationCode", {
                    data: {
                        countryCode,
                        phone: accountValue,
                        type,
                        verificationCode: values.code
                    }
                });
            } else {
                await createClientService().get("mtop.app.checkEmailCode", {
                    data: {
                        email: accountValue,
                        type,
                        verificationCode: values.code
                    }
                });
            }

            setVerificationCode(values.code);
            setForgotStatus(FORGOT_STATUS.PASSWORD);
        } catch (error) {

            if (error.errorFields) {
                return
            }

            const errorMsg = error?.msg || 'Verification failed';
            form.setFields([{ name: ['code'], errors: [errorMsg] }]);
        }
    }, [form, accountType, accountValue, countryCode]);

    // 处理密码重置
    const handleChangePassword = useCallback(async () => {
        try {
            // 只验证密码相关字段
            const values = await form.validateFields(['password', 'newPwd']);
            const { password, newPwd } = values;

            if (password !== newPwd) {
                message.error('The two passwords that you entered do not match!');
                form.setFields([{ name: ['newPwd'], errors: ['The two passwords that you entered do not match!'] }]);
                return;
            }

            if (accountType === ACCOUNT_TYPES.MOBILE) {
                await createClientService().post("mtop.user.resetPassword", {
                    mobile: accountValue,
                    newPwd,
                    countryCode,
                    verificationCode
                });
            } else {
                await createClientService().post("mtop.user.resetPasswordEmail", {
                    email: accountValue,
                    newPwd,
                    verificationCode
                });
            }

            message.success('Reset succeeded');
            setLoginStatus(LOGIN_STATUS.LOGIN);
        } catch (error) {
            // 只处理密码相关错误
            if (error.msg && (error.msg.includes('Password') || error.msg.includes('password'))) {
                form.setFields([{ name: ['password'], errors: [error.msg] }]);
            }
        }
    }, [form, accountType, accountValue, countryCode, verificationCode]);

    // 国家代码选择器
    const selectBefore = useMemo(() => {
        if (accountType === ACCOUNT_TYPES.MOBILE) {
            return (
                <Select
                    style={{ width: 150 }}
                    placeholder="Please select countryCode"
                    showSearch
                    popupMatchSelectWidth={false}
                    options={countryCodes}
                    value={countryCode}
                    onChange={setCountryCode}
                    fieldNames={{ value: 'code', label: 'code' }}
                    filterOption={(input, option) => {
                        const name = String(option?.name || '').toLowerCase();
                        const code = String(option?.code || '').toLowerCase();
                        const query = input.toLowerCase();
                        return name.includes(query) || code.includes(query);
                    }}
                    optionRender={(option) => (
                        <Space>
                            <Image src={option.data.flag} width={40} height={20} alt={option.data.name} />
                            {option.data.name}
                            <span>{option.data.code}</span>
                        </Space>
                    )}
                />
            );
        }
        return null;
    }, [accountType, countryCode]);

    // 验证码后缀
    const codeSuffix = useMemo(() => (
        <span
            style={{
                color: '#0091FF',
                cursor: isActive ? 'default' : 'pointer'
            }}
            onClick={isActive ? undefined : handleGetCode}
        >
            {isActive ? `${count}s` : 'Get Code'}
        </span>
    ), [isActive, count, handleGetCode]);

    // 表单项定义
    const accountInput = (
        <Form.Item
            className={`login-input-box ${inputStyles.account ? 'login-input-box-active' : ''}`}
            name="account"
            validateTrigger={['onChange', 'onBlur']}
            rules={[{ required: true, message: 'Please input your Mobile Number/Email!' }]}
        >
            <div className="inputInnerBox">
                {(inputStyles.account || form.getFieldValue('account')) && (
                    <span className="inputSide">Mobile Number/Email</span>
                )}
                {selectBefore}
                <Mentions
                    placeholder={`${inputStyles.account ? '' : 'Mobile Number/Email'}`}
                    split=""
                    value={accountValue}
                    onChange={handleAccountChange}
                    options={[
                        { key: 'gmail.com', value: 'gmail.com', label: `${filterEmail(accountValue)}@gmail.com` },
                        { key: 'yahoo.com', value: 'yahoo.com', label: `${filterEmail(accountValue)}@yahoo.com` },
                        { key: 'icloud.com', value: 'icloud.com', label: `${filterEmail(accountValue)}@icloud.com` },
                        { key: 'outlook.com', value: 'outlook.com', label: `${filterEmail(accountValue)}@outlook.com` },
                        { key: 'hotmail.com', value: 'hotmail.com', label: `${filterEmail(accountValue)}@hotmail.com` },
                        { key: 'qq.com', value: 'qq.com', label: `${filterEmail(accountValue)}@qq.com` },
                        { key: '163.com', value: '163.com', label: `${filterEmail(accountValue)}@163.com` },
                    ]}
                    onFocus={() => setInputFocus('account')}
                    onBlur={() => setInputBlur('account')}
                />
            </div>
        </Form.Item>
    );

    const passwordInput = (
        <Form.Item
            className={`login-input-box ${inputStyles.password ? 'login-input-box-active' : ''}`}
            name="password"
            rules={[
                { required: true, message: 'Please input your password!' },
                ...(loginStatus !== LOGIN_STATUS.LOGIN ? [{ validator: validatePasswordStrength }] : [])
            ]}
        >
            <div className="inputInnerBox">
                {(inputStyles.password || form.getFieldValue('password')) && (
                    <span className="inputSide">
                        {loginStatus === LOGIN_STATUS.FORGOT ? 'New ' : ''}Password
                    </span>
                )}
                <Input.Password
                    onFocus={() => setInputFocus('password')}
                    onBlur={() => setInputBlur('password')}
                    placeholder={`${inputStyles.password ? '' : `${loginStatus === LOGIN_STATUS.FORGOT ? 'New ' : ''}Password`}`}
                />
            </div>
        </Form.Item>
    );

    const confirmPasswordInput = (
        <Form.Item
            className={`login-input-box ${inputStyles.newPwd ? 'login-input-box-active' : ''}`}
            name="newPwd"
            dependencies={['password']}
            rules={[
                { required: true, message: 'Please confirm your password!' },
                ...(loginStatus === LOGIN_STATUS.FORGOT ? [{ validator: validatePasswordStrength }] : []),
                ({ getFieldValue }) => ({
                    validator(_, value) {
                        if (!value || getFieldValue('password') === value) {
                            return Promise.resolve();
                        }
                        return Promise.reject('The two passwords that you entered do not match!');
                    },
                })
            ]}
        >
            <div className="inputInnerBox">
                {(inputStyles.newPwd || form.getFieldValue('newPwd')) && (
                    <span className="inputSide">Confirm New Password</span>
                )}
                <Input.Password
                    onFocus={() => setInputFocus('newPwd')}
                    onBlur={() => setInputBlur('newPwd')}
                    placeholder={`${inputStyles.newPwd ? '' : 'Confirm New Password'}`}
                />
            </div>
        </Form.Item>
    );

    const codeInput = (
        <Form.Item
            className={`login-input-box ${inputStyles.code ? 'login-input-box-active' : ''}`}
            name="code"
            rules={[{ required: true, message: 'Please input your Verification Code!' }]}
        >
            <div className="inputInnerBox">
                {(inputStyles.code || form.getFieldValue('code')) && (
                    <span className="inputSide">Verification Code</span>
                )}
                <Input
                    suffix={codeSuffix}
                    onFocus={() => setInputFocus('code')}
                    onBlur={() => setInputBlur('code')}
                    placeholder={`${inputStyles.code ? '' : 'Verification Code'}`}
                />
            </div>
        </Form.Item>
    );

    // 密码要求说明
    const passwordRequirements = (
        <div className="password-requirements">
            <p className='p_desc'>· 8 to 20 characters</p>
            <p className='p_desc'>· At least two of following: letters, numbers, symbols</p>
        </div>
    );

    // 渲染不同状态下的内容
    const renderContent = () => {
        switch (loginStatus) {
            case LOGIN_STATUS.LOGIN:
                return (
                    <>
                        <div className='title'>{titleText}</div>
                        {accountInput}
                        {passwordInput}
                        <Form.Item>
                            <div className="loginBtn" onClick={handleLogin}>Log in</div>
                        </Form.Item>
                        <Flex align='center' justify='space-between'>
                            <div className="btm_text" onClick={() => setLoginStatus(LOGIN_STATUS.FORGOT)}>
                                Forgot password？
                            </div>
                            <div className="btm_text" onClick={() => setLoginStatus(LOGIN_STATUS.REGISTER)}>
                                Create an Account
                            </div>
                        </Flex>
                        {renderDefaultBottom()}
                    </>
                );

            case LOGIN_STATUS.REGISTER:
                return (
                    <>
                        <div className='title'>{titleText}</div>
                        <div className='sub_title'>Get started selling on YiwuTrade</div>
                        {accountInput}
                        {codeInput}
                        {passwordInput}
                        {passwordRequirements}
                        <Form.Item>
                            <div className="loginBtn" onClick={handleLogin}>Create Account</div>
                        </Form.Item>
                        <div className="btmTetx">
                            Already have an Account？
                            <span onClick={() => setLoginStatus(LOGIN_STATUS.LOGIN)}>Sign in</span>
                        </div>
                        {renderDefaultBottom()}
                    </>
                );

            case LOGIN_STATUS.FORGOT:
                return (
                    <>
                        <div className='title'>{titleText}</div>
                        {forgotStatus === FORGOT_STATUS.VERIFY ? (
                            <>
                                {accountInput}
                                {codeInput}
                                <Form.Item>
                                    <div className="loginBtn" onClick={handleContinueNext}>Continue</div>
                                </Form.Item>
                            </>
                        ) : (
                            <>
                                {passwordInput}
                                {confirmPasswordInput}
                                {passwordRequirements}
                                <Form.Item>
                                    <div className="loginBtn" onClick={handleChangePassword}>Submit</div>
                                </Form.Item>
                            </>
                        )}
                        <Flex align='center' justify='space-between'>
                            <div className="btm_text" onClick={() => setLoginStatus(LOGIN_STATUS.LOGIN)}>
                                <LeftOutlined style={{ fontSize: 12 }} /> Back to Login
                            </div>
                            <div className="btm_text" onClick={() => setLoginStatus(LOGIN_STATUS.REGISTER)}>
                                Create an Account
                            </div>
                        </Flex>
                    </>
                );

            default:
                return null;
        }
    };

    // 默认底部内容
    const renderDefaultBottom = () => (
        <>
            <Divider plain>
                <span style={{ color: '#999999', fontSize: 12 }}>Sign in with</span>
            </Divider>
            <Flex justify="center" align="center" className='google_login_bx'>
                <GoogleLoginButton
                    onLoginSuccess={handleLoginSuccess}
                    onLoginError={(error) => message.error(error?.msg || 'Google login failed')}
                />
            </Flex>
            <div className="btmTetx">
                By continuing, you agree to our
                <a
                    href="https://wykj.microants.cn/wywt/page/protocolUser.html?lang=en"
                    target="_blank"
                    style={{ color: '#0091FF' }}
                >
                    Terms of Use
                </a>
                and
                <br />
                <a
                    href="https://wykj.microants.cn/wywt/page/protocolPrivacy.html?lang=en"
                    target="_blank"
                    style={{ color: '#0091FF' }}
                >
                    Merchandise Partner Privacy Policy.
                </a>
            </div>
        </>
    );

    const handleCancel = () => {
        setOpen(false);
        onClose?.();
    };

    return (
        <div className="login-modal">
            <Modal
                wrapClassName="login-modal-wrap"
                centered
                open={open}
                footer={null}
                onCancel={handleCancel}
                width={900}
            >
                <GoogleOAuthProvider clientId="107009538183-ccv7ne8mc6an8bmiml652u42gglth1pj.apps.googleusercontent.com">
                    <Flex align='center' justify='center' className='login-modal-container'>
                        <Flex align='center' justify='center' flex={1} className='login-modal-bg'>
                            <Image
                                src="https://public-read-bkt-oss.oss-cn-hangzhou.aliyuncs.com/yibuy/admin/login_bg.png"
                                alt='login_bg'
                                width={380}
                                height={327}
                            />
                        </Flex>
                        <div className='login-modal-content'>
                            <Form className="formBox" variant="borderless" form={form}>
                                {renderContent()}
                            </Form>
                        </div>
                    </Flex>
                </GoogleOAuthProvider>
            </Modal>
        </div>
    );
}